'use client';

import type { RefObject } from 'react';
import CmsImage from '@/components/ui/CmsImage';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { AboutImage, AboutSectionHeading } from '@/types/aboutus';
import type { PncStat } from '@/types/people-and-culture';
import { formatPncStatValue } from '../lib/format-pnc-stat';
import HeroCounterValue from './HeroCounterValue';

interface PeopleHeroProps {
  sectionRef: RefObject<HTMLDivElement>;
  bannerMedia?: AboutImage | null;
  bannerHeading?: AboutSectionHeading[];
  bannerDescription?: string;
  bannerStats?: PncStat[];
}

function isAccentColor(color: string | null | undefined): boolean {
  if (!color) return false;
  const normalized = color.trim().toUpperCase();
  return normalized === '#EC1C24' || normalized === '#ED1C22' || normalized === '#E31E24';
}

export default function PeopleHero({
  sectionRef,
  bannerMedia,
  bannerHeading = [],
  bannerDescription,
  bannerStats = [],
}: PeopleHeroProps) {
  const description = bannerDescription?.trim() ? stripHtml(bannerDescription) : '';

  const renderHeading = (animate: boolean) =>
    bannerHeading.map((part, index) => {
      const accent = isAccentColor(part.textColor);
      return (
        <h1
          key={part.id ?? index}
          {...(animate ? { 'data-hero-text': true } : {})}
          className={`hero-title ${accent ? 'text-[#ed1c22]' : 'text-white'} max-w-xl`}
        >
          {part.text}
        </h1>
      );
    });

  const renderDescription = (animate: boolean) => {
    if (!description) return null;
    const heroTextProps = animate ? { 'data-hero-text': true } : {};
    return (
      <p {...heroTextProps} className="hero-subtitle mt-4 max-w-md text-white/80 sm:mt-5">
        {description}
      </p>
    );
  };

  const renderStats = (animate: boolean) => {
    if (bannerStats.length === 0) return null;
    const heroTextProps = animate ? { 'data-hero-text': true } : {};
    return (
      <div {...heroTextProps} className="mt-8 flex flex-wrap gap-6 sm:mt-10 sm:gap-10">
        {bannerStats.map((stat, index) => (
          <div key={stat.id}>
            <p className="text-2xl font-bold text-white sm:text-3xl md:text-4xl">
              <HeroCounterValue value={formatPncStatValue(stat)} delay={1 + index * 0.12} />
            </p>
            <p className="mt-1 text-[10px] font-medium uppercase tracking-wider text-white/55 sm:text-xs">
              {stat.statheading}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden bg-black text-white">
      {/* Mobile / Tablet: full-width image, content below */}
      <div className="flex flex-col bg-black lg:hidden">
        <div className="relative min-h-[52svh] w-full shrink-0 sm:min-h-[58svh]">
          <CmsImage
            src="/images/career_img2.jpg"
            media={bannerMedia}
            alt={bannerMedia?.alternativeText || 'Wonder Cement team'}
            fill
            priority
            loading="eager"
            className="object-cover object-center"
            sizes="100vw"
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
        <div className="px-4 py-10 sm:px-6 sm:py-12 md:px-8">
          <div className="mx-auto max-w-[640px]">
            {renderHeading(false)}
            {renderDescription(false)}
            {renderStats(false)}
          </div>
        </div>
      </div>

      {/* Desktop: full-bleed overlay hero */}
      <div className="relative hidden min-h-screen w-full overflow-hidden lg:block">
        <div className="hero-img-wrap absolute inset-0 scale-110">
          <CmsImage
            src="/images/career_img2.jpg"
            media={bannerMedia}
            alt={bannerMedia?.alternativeText || 'Wonder Cement team'}
            fill
            priority
            sizes="100vw"
            className="object-cover object-center"
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/10" />
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
          aria-hidden
        />

        <div className="relative z-10 flex min-h-screen flex-col justify-end">
          <div className="container mx-auto w-full max-sm:px-4 pb-10 sm:pb-12 md:pb-14">
            {renderHeading(true)}
            {renderDescription(true)}
            {renderStats(true)}
          </div>
        </div>
      </div>
    </section>
  );
}
